/**
 * DeviceConnectModal (spec 013): the body of the "Connect Device" modal
 * (ModalsContainer renders the wrapper).
 *
 * Opening it creates the device session once (`createSession` is idempotent,
 * so StrictMode's double mount makes one); a failure shows the error and
 * "Try again" and is not retried on its own. It shows the state of
 * `DevicePreviewContext`: "Creating session…", the QR code with the player
 * link and "Waiting for a device…", "N device(s) connected", or the error.
 * Closing the modal keeps the session; Disconnect ends it. While the device
 * plays a story (spec 013 v7) one line under the state says which.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useDevicePreview, type DeviceContent } from '../contexts/DevicePreviewContext';
import styles from './DeviceConnectModal.module.scss';

interface DeviceConnectModalProps {
  onClose: () => void;
}

export const devicesConnectedText = (n: number) => `${n} device${n === 1 ? '' : 's'} connected`;

/** The modal's line for a story run on the device. */
export const storyContentText = (content: DeviceContent): string | null => {
  if (content.kind !== 'story') return null;
  return content.startAtStorySceneId !== undefined ? `Playing the story from “${content.label ?? 'the selected scene'}”` : 'Playing the whole story';
};

const DeviceConnectModal: React.FC<DeviceConnectModalProps> = ({ onClose }) => {
  const { status, message, sessionId, qrCode, playerUrl, playerCount, content, createSession, disconnect } = useDevicePreview();
  const storyLine = content ? storyContentText(content) : null;
  const attempted = useRef(false);
  const [copied, setCopied] = useState(false);

  // One automatic attempt per open of the modal.
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    if (status === 'idle' || status === 'waiting' || status === 'connected') void createSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = async () => {
    if (!playerUrl) return;
    try {
      await navigator.clipboard.writeText(playerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // The link is selectable text as well.
    }
  };

  const handleDisconnect = () => {
    disconnect();
    onClose();
  };

  const showQr = (status === 'waiting' || status === 'connected') && qrCode;

  return (
    <div className={styles.body} data-testid="device-modal" data-status={status}>
      {status === 'creating' && (
        <div className={styles.loading}>
          <Spinner animation="border" />
          <p>Creating session…</p>
        </div>
      )}

      {status === 'error' && (
        <div className={styles.error}>
          <span className={styles.errorIcon}>⚠️</span>
          <p>{message}</p>
          <Button variant="outline-primary" size="sm" onClick={() => void createSession()}>
            Try again
          </Button>
        </div>
      )}

      {status === 'idle' && (
        <div className={styles.error}>
          {message && <p>{message}</p>}
          <Button variant="outline-primary" size="sm" onClick={() => void createSession()}>
            Connect a device
          </Button>
        </div>
      )}

      {showQr && (
        <div className={styles.qrContainer}>
          <div className={styles.qrCode}>
            <img src={qrCode!} alt="QR code for device connection" />
          </div>

          <div className={styles.instructions}>
            <p className={styles.scanText}>Scan with your phone camera</p>
            <a className={styles.urlText} href={playerUrl ?? undefined} target="_blank" rel="noreferrer" data-testid="device-player-url">
              {playerUrl}
            </a>
            <Button variant="link" size="sm" onClick={() => void copyLink()}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>

          <div className={styles.status}>
            <div
              className={`${styles.statusIndicator} ${status === 'connected' ? styles.connected : styles.connecting}`}
              data-testid="device-modal-status"
            >
              <span className={styles.dot} />
              <span>{status === 'connected' ? devicesConnectedText(playerCount) : 'Waiting for a device…'}</span>
            </div>
            {storyLine && (
              <div className={styles.contentLine} data-testid="device-modal-content">{storyLine}</div>
            )}
            {sessionId && (
              <div className={styles.sessionInfo}>
                <span className={styles.label}>Session:</span>
                <span className={styles.sessionId}>{sessionId}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        {sessionId && (
          <Button variant="outline-danger" size="sm" onClick={handleDisconnect}>
            Disconnect
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default DeviceConnectModal;
